import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CommentInput from "./CommentInput";
import { Heart, MessageCircle, Trash2, Loader2, X } from "lucide-react";
import {
  getScoops,
  getReactionOnComment,
  addReactionOnComment,
  deleteReactionOnComment,
} from "../../services/scoops.js";

import { isAuthenticated } from "../../utils/helpers.js";

// Reactions Modal Component
const ReactionsModal = ({
  isOpen,
  onClose,
  reactions,
  userReactions,
  onEmojiSelect,
}) => {
  const [activeTab, setActiveTab] = useState("all");

  if (!isOpen) return null;

  // Group reactions by type
  const groupedReactions = reactions.reduce((acc, reaction) => {
    const key = reaction.emojiid || reaction.id;
    if (!acc[key]) {
      acc[key] = {
        ...reaction,
        users: [],
      };
    }
    acc[key].users.push(reaction);
    return acc;
  }, {});

  const reactionTypes = Object.values(groupedReactions).sort(
    (a, b) => b.users.length - a.users.length
  );
  const totalReactions = reactions.length;

  const getFilteredReactions = () => {
    if (activeTab === "all") return reactions;
    return reactions.filter((r) => (r.emojiid || r.id) === activeTab);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Reactions</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab("all")}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "all"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <span>All</span>
            <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
              {totalReactions}
            </span>
          </button>
          {reactionTypes.map((reactionType) => (
            <button
              key={reactionType.emojiid || reactionType.id}
              onClick={() =>
                setActiveTab(reactionType.emojiid || reactionType.id)
              }
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === (reactionType.emojiid || reactionType.id)
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="text-lg">{reactionType.emoji}</span>
              <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                {reactionType.users.length}
              </span>
            </button>
          ))}
        </div>

        {/* Reactions List */}
        <div className="max-h-96 overflow-y-auto">
          {getFilteredReactions().map((reaction, index) => (
            <div
              key={`${reaction.emojiid || reaction.id}-${index}`}
              className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <img
                  src={reaction.avatarurl || "/placeholder.svg"}
                  alt={reaction.username || "User"}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium text-gray-900">
                    {`${reaction.firstname} ${reaction.lastname}` ||
                      "Anonymous User"}
                  </p>
                  {reaction.username && (
                    <p className="text-sm text-gray-500">
                      @{reaction.username}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">{reaction.emoji}</span>
                <span className="text-sm text-gray-500">{reaction.title}</span>
              </div>
            </div>
          ))}
          {getFilteredReactions().length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <p>No reactions yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Comment = ({
  scoopid,
  postid,
  comment,
  onReply,
  onLike,
  onDelete,
  isReply = false,
}) => {
  const [isReplying, setIsReplying] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isAuthor, setIsAuthor] = useState(false);
  const [optimisticReplies, setOptimisticReplies] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [scoopEmojis, setScoopEmojis] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isLoadingEmojis, setIsLoadingEmojis] = useState(false);
  const [selectedEmojis, setSelectedEmojis] = useState({});
  const [selectedEmoji, setSelectedEmoji] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [commentReactions, setCommentReactions] = useState([]);
  const [userReactions, setUserReactions] = useState([]);
  const [totalReactions, setTotalReactions] = useState(0);
  const [isLoadingReactions, setIsLoadingReactions] = useState(false);
  const [isProcessingReaction, setIsProcessingReaction] = useState(false);
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchProfile() {
      try {
        const profile = localStorage.getItem("profile");
        if (profile) {
          const parsedProfile = JSON.parse(profile);
          setUserData(parsedProfile);
          setIsAuthor(parsedProfile.username === comment.author.username);
        }
      } catch (error) {
        console.error("Error while fetching profile", error);
      }
    }

    if (!userData) {
      fetchProfile();
    }
  }, [userData, comment.author.username]);

  useEffect(() => {
    async function fetchScoopEmojis() {
      if (!scoopid) return;

      setIsLoadingEmojis(true);
      try {
        const scoopsData = await getScoops();
        const currentScoop = scoopsData.find(
          (scoop) => scoop.scoopid === scoopid
        );
        if (currentScoop && currentScoop.emojis) {
          setScoopEmojis(currentScoop.emojis);
        }
      } catch (error) {
        console.error("Error fetching scoop emojis:", error);
      } finally {
        setIsLoadingEmojis(false);
      }
    }

    fetchScoopEmojis();
  }, [scoopid]);

  useEffect(() => {
    const fetchCommentReactions = async () => {
      if (!comment?.id) return;
      setIsLoadingReactions(true);
      try {
        const response = await getReactionOnComment(comment.id);
        if (response?.status === "success") {
          setCommentReactions(response.data.reactions || []);
          setUserReactions(response.data.userReactions || []);
          setTotalReactions(response.data.totalReactions || 0);

          if (response.data.userReactions?.length > 0) {
            setSelectedEmoji(response.data.userReactions[0]);
          } else {
            setSelectedEmoji(null);
          }
        }
      } catch (error) {
        console.error("Error fetching comment reactions:", error);
      } finally {
        setIsLoadingReactions(false);
      }
    };

    fetchCommentReactions();
  }, [comment?.id]);

  const refreshReactions = async () => {
    if (!comment?.id) return;

    try {
      const response = await getReactionOnComment(comment.id);
      if (response?.status === "success") {
        setCommentReactions(response.data.reactions || []);
        setUserReactions(response.data.userReactions || []);
        setTotalReactions(response.data.totalReactions || 0);

        if (response.data.userReactions?.length > 0) {
          setSelectedEmoji(response.data.userReactions[0]);
        } else {
          setSelectedEmoji(null);
        }
      }
    } catch (error) {
      console.error("Error refreshing reactions:", error);
    }
  };

  const handleReply = async (text, image) => {
    if (!userData) return;

    const optimisticReply = {
      id: `temp-${Date.now()}`,
      content: text,
      image: image ? URL.createObjectURL(image) : null,
      author: {
        id: userData.id,
        name: userData.name,
        username: userData.username,
        avatar: userData.avatar,
      },
      timestamp: "now",
      likes: 0,
      likedByCurrentUser: false,
      isOptimistic: true,
      isLoading: true,
    };

    setOptimisticReplies((prev) => [...prev, optimisticReply]);
    setIsReplying(false);

    try {
      const actualReply = await onReply(comment.id, text, image);

      setOptimisticReplies((prev) =>
        prev.filter((reply) => reply.id !== optimisticReply.id)
      );

      if (optimisticReply.image && image) {
        URL.revokeObjectURL(optimisticReply.image);
      }
    } catch (error) {
      setOptimisticReplies((prev) =>
        prev.filter((reply) => reply.id !== optimisticReply.id)
      );

      if (optimisticReply.image && image) {
        URL.revokeObjectURL(optimisticReply.image);
      }
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(comment.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Failed to delete comment:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLike = async () => {
    try {
      await onLike(comment.id);
    } catch (error) {
      console.error("Failed to like comment:", error);
    }
  };

  const handleEmojiSelect = async (emoji) => {
    if (isProcessingReaction) return;
    if (!isAuthenticated()) {
      navigate("/signin");
      return;
    }
    setIsProcessingReaction(true);

    try {
      const isCurrentlySelected =
        selectedEmoji && selectedEmoji.emojiid === emoji.emojiid;

      if (!isCurrentlySelected) {
        setSelectedEmoji(emoji);
      }

      const body = {
        commentId: comment.id,
        emojiId: emoji.emojiid,
      };

      await addReactionOnComment(body);

      await refreshReactions();

      setTimeout(() => {
        setShowEmojiPicker(false);
      }, 100);
    } catch (error) {
      console.error("Failed to add reaction on comment:", error);
      if (selectedEmoji && selectedEmoji.emojiid === emoji.emojiid) {
        setSelectedEmoji(null);
      }
    } finally {
      setIsProcessingReaction(false);
    }
  };

  const handleSelectedEmojiClick = async () => {
    if (!selectedEmoji || isProcessingReaction) return;
    if (!isAuthenticated()) {
      navigate("/signin");
      return;
    }

    setIsProcessingReaction(true);
    const previousSelectedEmoji = selectedEmoji;

    try {
      const previousSelectedEmoji = selectedEmoji;
      setSelectedEmoji(null);

      await deleteReactionOnComment({
        commentId: comment.id,
        emojiId: selectedEmoji.id,
      });

      setShowEmojiPicker(false);
      await refreshReactions();
    } catch (error) {
      console.error("Failed to delete reaction:", error);
      setSelectedEmoji(previousSelectedEmoji);
    } finally {
      setIsProcessingReaction(false);
    }
  };

  const handleMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (scoopEmojis.length > 0 && !isProcessingReaction) {
      setShowEmojiPicker(true);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    const timeout = setTimeout(() => {
      setShowEmojiPicker(false);
    }, 300);
    setHoverTimeout(timeout);
  };

  // Fixed mobile touch handling for like functionality
  const handleTouchStart = (e) => {
    if (scoopEmojis.length > 0 && !isProcessingReaction) {
      setShowEmojiPicker(true);
    }
  };

  const handleLikeTouch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedEmoji) {
      handleSelectedEmojiClick();
    }
  };

  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  const allReplies = [...(comment.replies || []), ...optimisticReplies];

  // Group reactions for display (Facebook style)
  const groupedReactions = commentReactions.reduce((acc, reaction) => {
    const key = reaction.emojiid || reaction.id;
    if (!acc[key]) {
      acc[key] = {
        ...reaction,
        count: 0,
        hasUserReaction: false,
      };
    }
    acc[key].count += 1;
    if (userReactions.some((ur) => (ur.emojiid || ur.id) === key)) {
      acc[key].hasUserReaction = true;
    }
    return acc;
  }, {});

  const topReactions = Object.values(groupedReactions)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3); // Show top 3 reaction types

  return (
    <div
      className={`py-3 px-2 sm:px-4 border-b border-gray-200 last:border-b-0 group ${
        comment.isOptimistic ? "opacity-75" : ""
      }`}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <img
            src={comment.author.avatar || "/placeholder.svg"}
            alt={comment.author.name}
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
          />
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center flex-wrap gap-1 min-w-0 flex-1">
              <span className="font-bold text-sm truncate max-w-[120px] sm:max-w-none">
                {comment.author.name}
              </span>
              {comment.author.username && (
                <span className="text-gray-500 text-sm truncate max-w-[100px] sm:max-w-none">
                  @{comment.author.username}
                </span>
              )}
              <span className="text-gray-500 text-xs sm:text-sm whitespace-nowrap">
                · {comment.timestamp}
              </span>
              {comment.isLoading && (
                <Loader2
                  size={12}
                  className="animate-spin text-gray-400 ml-1"
                />
              )}
            </div>

            {/* Delete button */}
            <div className="relative flex-shrink-0">
              {isAuthor &&
                (!showDeleteConfirm ? (
                  <button
                    className="opacity-0 group-hover:opacity-100 hover:opacity-100 flex items-center text-gray-400 hover:text-red-500 transition-all p-1.5 rounded-full hover:bg-red-50"
                    onClick={() => setShowDeleteConfirm(true)}
                    title="Delete comment"
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </button>
                ) : (
                  <div className="flex items-center gap-1">
                    <button
                      className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition disabled:opacity-50"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting..." : "Delete"}
                    </button>
                    <button
                      className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                  </div>
                ))}
            </div>
          </div>

          {/* Content */}
          <div className="mt-2">
            {comment.image && (
              <div className="mt-2 mb-2 rounded-lg overflow-hidden">
                <img
                  src={comment.image}
                  alt="Comment attachment"
                  className="w-full h-auto  object-cover rounded-lg"
                />
              </div>
            )}

            <p className="text-sm leading-relaxed mb-2 break-words whitespace-pre-wrap">
              {comment.content}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between border-t border-gray-100 pt-2">
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Like/Reaction button with improved mobile support */}
              <div
                className="relative"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
              >
                <button
                  className={`text-xs transition font-medium flex items-center gap-1 disabled:opacity-50 px-2 py-1 rounded-full hover:bg-gray-100 touch-manipulation ${
                    selectedEmoji
                      ? "text-blue-500 hover:text-blue-600 bg-blue-50"
                      : "text-gray-500 hover:text-blue-500"
                  }`}
                  onClick={selectedEmoji ? handleSelectedEmojiClick : undefined}
                  onTouchEnd={selectedEmoji ? handleLikeTouch : undefined}
                  disabled={isProcessingReaction}
                >
                  {selectedEmoji ? (
                    <>
                      <span className="text-base">{selectedEmoji.emoji}</span>
                      <span className=" sm:inline">{selectedEmoji.title}</span>
                      {isProcessingReaction && (
                        <Loader2 size={10} className="animate-spin ml-1" />
                      )}
                    </>
                  ) : (
                    <>
                      <span>Like</span>
                      {isProcessingReaction && (
                        <Loader2 size={10} className="animate-spin ml-1" />
                      )}
                    </>
                  )}
                </button>

                {/* Enhanced Emoji Picker with better mobile support */}
                {showEmojiPicker &&
                  scoopEmojis.length > 0 &&
                  !isProcessingReaction && (
                    <div
                      className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-white border border-gray-200 rounded-full shadow-2xl px-3 py-2 z-50 animate-in fade-in zoom-in duration-200"
                      style={{
                        boxShadow: "0 16px 48px rgba(0, 0, 0, 0.15)",
                      }}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                      onTouchStart={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-2">
                        {scoopEmojis.map((emojiData) => (
                          <button
                            key={emojiData.emojiid}
                            className={`relative p-2 hover:scale-125 transition-all duration-200 text-2xl hover:bg-gray-50 rounded-full group/emoji transform active:scale-110 touch-manipulation ${
                              selectedEmoji &&
                              selectedEmoji.emojiid === emojiData.emojiid
                                ? "bg-blue-50 ring-2 ring-blue-200 scale-110"
                                : ""
                            }`}
                            onClick={() => handleEmojiSelect(emojiData)}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEmojiSelect(emojiData);
                            }}
                            title={emojiData.title}
                            onMouseDown={(e) => e.preventDefault()}
                            disabled={isProcessingReaction}
                          >
                            {emojiData.emoji}
                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover/emoji:opacity-100 transition-all duration-200 whitespace-nowrap pointer-events-none z-10 shadow-lg">
                              {emojiData.title}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-3 border-r-3 border-t-3 border-transparent border-t-gray-900" />
                            </div>
                          </button>
                        ))}
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-6 border-r-6 border-t-6 border-transparent border-t-white drop-shadow-sm" />
                    </div>
                  )}
              </div>

              {totalReactions > 0 && (
                <div className="flex items-center justify-between ">
                  <button
                    onClick={() => setShowReactionsModal(true)}
                    className="flex items-center gap-2 hover:underline transition-all duration-200 group"
                  >
                    {/* Improved Reaction icons stack with better contrast and alignment */}
                    <div className="flex items-center">
                      {topReactions.map((reaction, index) => (
                        <div
                          key={reaction.emojiid || reaction.id}
                          className="w-6 h-6 bg-white rounded-full flex items-center justify-center text-sm border-2 border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200"
                          style={{
                            zIndex: topReactions.length - index,
                            marginLeft: index > 0 ? "-4px" : "0px",
                          }}
                        >
                          {reaction.emoji}
                        </div>
                      ))}
                    </div>
                    <span className="text-sm text-gray-600 font-medium">
                      {totalReactions}{" "}
                    </span>
                  </button>
                </div>
              )}

              {/* Reply button */}
              {!isReply && (
                <button
                  className="flex items-center text-gray-500 hover:text-blue-500 transition p-1 rounded-full hover:bg-blue-50 touch-manipulation"
                  onClick={() => setIsReplying(!isReplying)}
                >
                  <MessageCircle size={16} />
                  <span className="ml-1 text-xs hidden sm:inline">Reply</span>
                </button>
              )}
            </div>
          </div>

          {/* Reply input */}
          {isReplying && !isReply && (
            <div className="mt-3">
              <CommentInput
                isReply={true}
                onSubmit={handleReply}
                replyingTo={comment.author.username}
              />
            </div>
          )}

          {/* Replies */}
          {allReplies.length > 0 && (
            <div className="mt-3 pl-2 sm:pl-4 border-l-2 border-gray-200">
              {allReplies.map((reply) => (
                <Comment
                  key={reply.id}
                  scoopid={scoopid}
                  postid={postid}
                  comment={reply}
                  onReply={onReply}
                  onLike={onLike}
                  onDelete={onDelete}
                  isReply={true}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reactions Modal */}
      <ReactionsModal
        isOpen={showReactionsModal}
        onClose={() => setShowReactionsModal(false)}
        reactions={commentReactions}
        userReactions={userReactions}
        onEmojiSelect={handleEmojiSelect}
      />
    </div>
  );
};

export default Comment;
